import * as React from 'react';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../../reducers';
import { updateEditableMedia, EditableMediaData } from '../../../../containers/Editor/actions';
import { PositionAndSize, getNumberFromString } from '../../../../containers/Editor/utils/helpers';
import MediaRnd from '../MediaRnd';
import { EditableType, EditorType } from '../../../../containers/Editor/constants';
import Utils from '../../../../utils';

interface MediaItemProps {
	editorType: EditorType;
	type: EditableType;
	src: string;
	scale: number;
	isEditedFromBackoffice: boolean;
	isActive: boolean;
	isLoaded: boolean;
	onAfterLoad: () => void;
	page: number;
	id: string;
	frameWidth: number;
	frameHeight: number;
	width: number;
	height: number;
	x: number;
	y: number;
	canDragAndResize: boolean;
	isKeyActionsDisabled: boolean;
	dispatch: Dispatch<StoreState>;
	isCustom?: boolean;
	isDefaultMedia?: boolean;
	parentContainer?: Element | null;
	iframeContainer?: HTMLIFrameElement | null;
	frameAspectRatio?: number;
	onLoad?: (width: number, height: number) => void;
	onClick?: () => void;
	onClickOutside?: (positionAndSize: PositionAndSize) => void;
	isAccessibleVersion?: boolean;
	originalStyle?: string;
}

const getDefaultImage = (type: EditableType): string => {
	switch (type) {
		case EditableType.animation:
			return 'iconset_uprez_animation';
		case EditableType.video:
			return 'iconset_uprez_play-button';
		case EditableType.image:
		default:
			return 'iconset_uprez_image';
	}
};

const MediaItem = ({
	editorType,
	type,
	src,
	scale,
	isEditedFromBackoffice,
	isActive,
	isLoaded,
	isDefaultMedia,
	onAfterLoad,
	page,
	id,
	frameWidth,
	frameHeight,
	width,
	height,
	x,
	y,
	canDragAndResize,
	isKeyActionsDisabled,
	dispatch,
	isCustom,
	parentContainer,
	iframeContainer,
	frameAspectRatio,
	onLoad,
	onClick,
	onClickOutside,
	isAccessibleVersion,
	originalStyle,
}: MediaItemProps) => {
	const imgElement = React.useRef<HTMLImageElement>(null);
	const [isError, setIsError] = React.useState(false);

	const handleLoad = () => {
		setIsError(false);

		if (isLoaded) {
			return;
		}

		if (isCustom && onLoad) {
			const { naturalWidth, naturalHeight } = imgElement.current as HTMLImageElement;
			onLoad(naturalWidth, naturalHeight);
		} else {
			dispatch(updateEditableMedia(page, id, {
				isLoaded: true,
				isEditedFromBackoffice,
			}, false, false));
		}

		onAfterLoad();
	};

	const handleClick = () => {
		if (editorType !== EditorType.HTML || !parentContainer) {
			return;
		}

		const {
			width: frameWidth,
			height: frameHeight,
		} = parentContainer.getBoundingClientRect();
		const {
			naturalWidth,
			naturalHeight,
			style: { width, top, left },
		} = imgElement.current as HTMLImageElement;
		const imageAspectRatio = naturalHeight / naturalWidth;
		const data: EditableMediaData = {
			frameWidth,
			frameHeight,
			width: getNumberFromString(width as string),
			height: ((frameWidth * getNumberFromString(width as string)) * imageAspectRatio) / frameHeight,
			x: getNumberFromString(left as string),
			y: getNumberFromString(top as string),
			isEditedFromBackoffice,
		};

		if (isDefaultMedia) {
			// keep aspect ratio from default media to calculate height of frame in <MediaEditor>
			// and use it on Reader side too (via data-uprez-aspect-ratio attribute)
			data.frameAspectRatio = imageAspectRatio;
		}

		dispatch(updateEditableMedia(page, id, data, false));

		if (onClick) {
			onClick();
		}
	};

	// set frame sizes for custom media on component mount
	// it can be done only when parentContainer is rendered
	// IntersectionObserver is used because parentContainer can be hidden on mount (e.g., in slider) so its width equals 0 which causes incorrect calculation
	React.useEffect(() => {
		if (editorType !== EditorType.HTML || !parentContainer || isDefaultMedia) {
			return;
		}

		const handleIntersect = (entries: IntersectionObserverEntry[]) => {
			entries.forEach(({ isIntersecting }) => {
				if (isIntersecting) {
					const { width: frameWidth } = parentContainer.getBoundingClientRect();

					dispatch(updateEditableMedia(page, id, {
						frameWidth,
						frameHeight: frameWidth * (frameAspectRatio as number),
					}, false));
				}
			});
		};

		const observer = new IntersectionObserver(handleIntersect);
		observer.observe(parentContainer);

		return () => {
			observer.unobserve(parentContainer);
		};
	}, []);

	const hasImageStyle = editorType === EditorType.HTML &&
		type === EditableType.image &&
		!isActive &&
		!isDefaultMedia;

	let imageStyle: React.CSSProperties | undefined;

	if (isAccessibleVersion && originalStyle && type === EditableType.image) {
		const styleObj: React.CSSProperties = {};

		originalStyle.split(';').forEach(rule => {
			const [key, value] = rule.split(':').map(s => s.trim());

			if (key && value) {
				// Convert CSS property names to camelCase for React
				const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
				styleObj[camelKey] = value;
			}
		});

		imageStyle = styleObj;
	} else if (hasImageStyle) {
		imageStyle = {
			position: 'absolute',
			width: `${width}%`,
			height: 'auto',
			top: `${y}%`,
			left: `${x}%`,
		};
	}

	const Image = (
		<>
			<img
				// prop key is used to remove previous loaded image in order to display new image uploading
				key={src}
				ref={imgElement}
				src={src}
				alt=""
				hidden={isError}
				onLoad={handleLoad}
				onError={() => setIsError(true)}
				onDragStart={e => e.preventDefault()}
				onClick={handleClick}
				style={imageStyle}
			/>
			{isError && Utils.getSvgIcon(getDefaultImage(type), { onClick: handleClick })}
		</>
	);

	if (canDragAndResize) {
		return (
			<MediaRnd
				id={id}
				page={page}
				frameWidth={frameWidth}
				frameHeight={frameHeight}
				width={width}
				height={height}
				x={x}
				y={y}
				scale={scale}
				isActive={isActive}
				isLoaded={isLoaded}
				isKeyActionsDisabled={isKeyActionsDisabled}
				dispatch={dispatch}
				isCustom={isCustom}
				onClickOutside={onClickOutside}
				iframeContainer={iframeContainer}
				isPercentageValues={editorType === EditorType.HTML}
			>
				{Image}
			</MediaRnd>
		);
	}

	return Image;
};

export default MediaItem;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/MediaEditor/MediaItem/index.tsx